import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, QrCode, Sparkles } from "lucide-react";
import { galleryImages } from "../../mock";

const PhoneMockup = ({ tilt, children }) => (
  <div className={`relative ${tilt} transition-transform duration-500 hover:rotate-0`}>
    <div className="w-[220px] md:w-[260px] aspect-[9/19] bg-navy-800 rounded-[2.5rem] p-2 shadow-2xl ring-1 ring-black/10">
      <div className="w-full h-full bg-white rounded-[2rem] overflow-hidden relative">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-navy-800 rounded-full z-10" />
        {children}
      </div>
    </div>
  </div>
);

const Hero = () => {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-cream-100 via-cream-50 to-cream-100">
      {/* Decorative blobs */}
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-coral-100/40 blur-3xl" />
      <div className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-coral-200/30 blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 pt-14 lg:pt-20 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 bg-coral-300/15 text-coral-500 px-3 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase mb-6">
              <Sparkles className="w-3.5 h-3.5" /> The Original QR Code Photo Sharing Platform
            </div>
            <h1 className="font-serif text-navy-700 text-[42px] md:text-[58px] lg:text-[64px] leading-[1.05] tracking-tight">
              Private <span className="italic text-coral-300">QR Code</span> <br />Photo Sharing &amp;<br />
              <span className="italic">Digital Guestbook</span>
            </h1>
            <p className="mt-6 text-navy-700/75 text-lg max-w-xl leading-relaxed">
              Capture every angle of your event. Guests just scan, upload &amp; share — no apps, no sign-ups. Because it only happens once.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/get-started" className="group inline-flex items-center gap-2 bg-coral-300 text-white px-7 py-3.5 rounded-full font-semibold hover:bg-coral-400 transition-colors soft-shadow-coral">
                Get Started Free
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/how-it-works" className="inline-flex items-center gap-2 text-navy-700 font-semibold hover:text-coral-300 transition-colors">
                <span className="w-10 h-10 rounded-full border border-navy-700/30 flex items-center justify-center group-hover:border-coral-300">
                  <QrCode className="w-4 h-4" />
                </span>
                See how it works
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-navy-700/60">
              <div className="flex -space-x-3">
                {galleryImages.slice(0, 4).map((g) => (
                  <img key={g} src={g} alt="" className="w-10 h-10 rounded-full ring-2 ring-cream-100 object-cover" />
                ))}
              </div>
              <div>
                <div className="font-semibold text-navy-700">150k+ events worldwide</div>
                <div className="text-xs">Trusted by hosts in 100+ countries</div>
              </div>
            </div>
          </div>

          {/* Phone showcase */}
          <div className="relative flex justify-center items-center h-[480px] md:h-[560px]">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-[420px] h-[420px] rounded-full bg-gradient-to-br from-coral-100 to-cream-200 blur-2xl opacity-70" />
            </div>
            <div className="relative flex items-center gap-[-30px]">
              <PhoneMockup tilt="phone-tilt-1 -mr-10 z-10">
                <div className="h-full bg-gradient-to-b from-cream-200 to-cream-100 p-3 pt-8 flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <img src={galleryImages[0]} alt="" className="w-9 h-9 rounded-full object-cover" />
                    <div className="flex-1">
                      <div className="text-[10px] font-serif italic text-navy-700">Madison &amp; Aiden</div>
                      <div className="text-[8px] text-navy-700/60">June 12, 2026</div>
                    </div>
                  </div>
                  <div className="flex-1 rounded-lg overflow-hidden relative">
                    <img src={galleryImages[1]} alt="" className="w-full h-full object-cover" />
                  </div>
                  <button className="mt-2 bg-coral-300 text-white text-[10px] py-1.5 rounded-md font-semibold">Add to gallery</button>
                </div>
              </PhoneMockup>
              <PhoneMockup tilt="phone-tilt-2 -ml-10 z-0">
                <div className="h-full bg-white p-3 pt-8">
                  <div className="text-[10px] font-semibold text-navy-700 mb-2">All photos</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {galleryImages.slice(0, 6).map((g, i) => (
                      <div key={g} className="aspect-square rounded-md overflow-hidden">
                        <img src={g} alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              </PhoneMockup>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
